import { useCallback } from "react";
import { TestDetails } from "../../../../hooks/test/types/types";
import {
  Objective,
  QuestionDetails,
} from "../../../../hooks/questions/types/types";

/**
 * Extrai os valores dos atributos "src" encontrados em elementos <img> dentro do HTML.
 */
function parseHtmlString(html: string): string[] {
  const element = document.createElement("div");
  element.innerHTML = html;
  const srcs: string[] = [];
  const imgs = element.getElementsByTagName("img");
  for (let i = 0; i < imgs.length; i++) {
    const urlValue = imgs[i].getAttribute("src");
    if (urlValue) {
      srcs.push(urlValue);
    }
  }
  return srcs;
}

/**
 * Hook para converter imagens referenciadas por URL em DataURL (base64) e atualizar as questões.
 */
export default function useInitialImages() {
  /**
   * Converte uma URL de imagem em uma string base64.
   */
  const convertImageToBase64 = useCallback(
    (src: string, outputFormat = "image/png"): Promise<string> => {
      return new Promise<string>((resolve) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas") as HTMLCanvasElement;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            ctx.drawImage(img, 0, 0);
            const dataURL = canvas.toDataURL(outputFormat);
            resolve(dataURL);
          } else {
            console.warn("Não foi possível obter contexto 2D do canvas");
            resolve(src);
          }
        };
        img.onerror = () => {
          console.error("Falha ao carregar imagem:", src);
          resolve(src);
        };
        img.src = src;
      });
    },
    []
  );

  /**
   * Processa as alternativas (objetivos) de uma subquestão, convertendo as imagens contidas em seu texto.
   */
  const processAlternativas = useCallback(
    async (alternatives: Objective[]): Promise<Objective[]> => {
      const updatedAlternatives: Objective[] = [];
      for (const alt of alternatives) {
        const srcs = parseHtmlString(alt.text.toString());
        let altTextProcessed = alt.text;
        for (const src of srcs) {
          const dataURL = await convertImageToBase64(src);
          // Atualiza o texto substituindo a URL original pelo DataURL
          altTextProcessed = new Text(
            altTextProcessed.toString().replace(src, dataURL)
          );
        }
        updatedAlternatives.push({ ...alt, text: altTextProcessed });
      }
      return updatedAlternatives;
    },
    [convertImageToBase64]
  );

  /**
   * Processa uma questão simples (objective), atualizando o enunciado e, se existir, as alternativas da primeira subquestão.
   */
  const processQuestaoSimples = useCallback(
    async (questao: QuestionDetails): Promise<QuestionDetails> => {
      const updatedQuestao = { ...questao };

      // Processa o enunciado (statement)
      if (updatedQuestao.statement) {
        const enunciadoSrcs = parseHtmlString(updatedQuestao.statement);
        let enunciadoProcessed = updatedQuestao.statement;
        for (const src of enunciadoSrcs) {
          const dataURL = await convertImageToBase64(src);
          enunciadoProcessed = enunciadoProcessed.replace(src, dataURL);
        }
        updatedQuestao.statement = enunciadoProcessed;
      }

      // Processa as alternativas da primeira subquestão, se existirem
      if (
        updatedQuestao.subQuestions &&
        updatedQuestao.subQuestions.length > 0
      ) {
        const firstSub = updatedQuestao.subQuestions[0];
        if (firstSub.objectives && firstSub.objectives.length > 0) {
          const updatedObjectives = await processAlternativas(
            firstSub.objectives
          );
          const updatedSubQuestion = {
            ...firstSub,
            objectives: updatedObjectives,
          };
          updatedQuestao.subQuestions = [
            updatedSubQuestion,
            ...updatedQuestao.subQuestions.slice(1),
          ];
        }
      }

      return updatedQuestao;
    },
    [convertImageToBase64, processAlternativas]
  );

  /**
   * Processa uma questão do tipo "set" (conjunto): atualiza o enunciado principal e, para cada subquestão,
   * converte as imagens presentes no enunciado e, se houver, nas alternativas.
   */
  const processQuestaoConjunto = useCallback(
    async (questao: QuestionDetails): Promise<QuestionDetails> => {
      const updatedQuestao = { ...questao };

      // Processa o enunciado principal
      if (updatedQuestao.statement) {
        const enunciadoSrcs = parseHtmlString(updatedQuestao.statement);
        let enunciadoProcessed = updatedQuestao.statement;
        for (const src of enunciadoSrcs) {
          const dataURL = await convertImageToBase64(src);
          enunciadoProcessed = enunciadoProcessed.replace(src, dataURL);
        }
        updatedQuestao.statement = enunciadoProcessed;
      }

      // Processa cada subquestão
      updatedQuestao.subQuestions = await Promise.all(
        updatedQuestao.subQuestions.map(async (subQ: any) => {
          const updatedSubQ = { ...subQ };
          if (updatedSubQ.statement) {
            const subSrcs = parseHtmlString(updatedSubQ.statement);
            let subProcessed = updatedSubQ.statement;
            for (const src of subSrcs) {
              const dataURL = await convertImageToBase64(src);
              subProcessed = subProcessed.replace(src, dataURL);
            }
            updatedSubQ.statement = subProcessed;
          }
          if (updatedSubQ.objectives && updatedSubQ.objectives.length > 0) {
            updatedSubQ.objectives = await processAlternativas(
              updatedSubQ.objectives
            );
          }
          return updatedSubQ;
        })
      );

      return updatedQuestao;
    },
    [convertImageToBase64, processAlternativas]
  );

  /**
   * Função principal: recebe uma prova (TestDetails) e retorna uma prova com todas as imagens convertidas.
   * Se a prova não possuir questões, ela é retornada inalterada.
   */
  const processImages = useCallback(
    async (test: TestDetails): Promise<TestDetails> => {
      if (
        !test?.questions ||
        !Array.isArray(test.questions) ||
        test.questions.length === 0
      ) {
        return test;
      }

      const updatedQuestions = [];
      for (const questao of test.questions) {
        // Define o tipo de questão a partir de sua estrutura:
        const isSet = questao.subQuestions.length > 1 && !!questao.statement;
        const isObjective =
          questao.subQuestions.length > 0 &&
          questao.subQuestions[0].objectives !== undefined;

        if (isSet) {
          updatedQuestions.push(await processQuestaoConjunto(questao));
        } else if (isObjective) {
          updatedQuestions.push(await processQuestaoSimples(questao));
        } else {
          updatedQuestions.push(questao);
        }
      }

      return { ...test, questions: updatedQuestions };
    },
    [processQuestaoSimples, processQuestaoConjunto]
  );

  return {
    convertImageToBase64,
    processImages,
  };
}
